import React, {
  type KeyboardEventHandler,
  forwardRef,
  type InputHTMLAttributes,
  type ReactNode,
  type Ref,
  type ChangeEvent,
} from "react";

import clsx from "clsx";
import { get } from "lodash";

export interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  name?: string;
  label?: string | React.ReactElement;
  value?: string;
  defaultValue?: string;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  className?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  width?: string;
  maxWidth?: string;
  errorMessage?: string;
  errors?: any;
  isDisabled?: boolean;
  isRequired?: boolean;
  inputClassName?: string;
  errorClassName?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      name,
      label,
      value,
      defaultValue,
      onChange,
      className,
      inputClassName,
      errorClassName,
      leftIcon = null,
      rightIcon = null,
      width,
      maxWidth,
      errorMessage,
      errors,
      isDisabled = false,
      isRequired = false,
      onKeyDown,
      ...props
    },
    ref: Ref<any>,
  ) => {
    // Access the error message from react-hook-form
    const formErrorMessage = get(errors, `${name}.message`);
    const hasError = errorMessage || formErrorMessage;

    return (
      <div
        className={clsx("nb-input-container group/input", className, {
          "pointer-events-none": isDisabled,
          "has-label": label,
        })}
        style={{ width, maxWidth }}
      >
        {label && (
          <label className={clsx("label", isDisabled && "text-disabled")}>
            {label}
            {isRequired && <span className="text-mars">*</span>}
          </label>
        )}
        <span
          className={clsx(
            leftIcon &&
              clsx(
                "absolute-vertical-center group-hover/input:text-cosmic",
                "group-focus-within/input:text-cosmic",
              ),
          )}
        >
          {leftIcon && <span className="pl-2.5">{leftIcon}</span>}
        </span>
        <input
          name={name}
          ref={ref}
          className={clsx(
            "nb-input bg-v2-primary text-v2-primary border-v2-secondary",
            rightIcon && "pr-10",
            leftIcon && "pl-10",
            inputClassName,
            {
              /* eslint-disable-next-line max-len */
              "[-moz-appearance:_textfield] [&::-webkit-inner-spin-button]:m-0 [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:m-0 [&::-webkit-outer-spin-button]:appearance-none":
                props?.type === "number",
              "opacity-60": isDisabled,
            },
          )}
          type="text"
          onKeyDown={onKeyDown}
          defaultValue={defaultValue}
          value={value}
          onChange={onChange}
          disabled={isDisabled}
          required={isRequired}
          {...props}
        />
        <span className={clsx(rightIcon && "absolute-vertical-center right-0")}>
          {rightIcon && <span className="pr-2.5">{rightIcon}</span>}
        </span>
        {hasError && (
          <div className={clsx("error-message", errorClassName)}>
            {errorMessage ?? formErrorMessage}
          </div>
        )}
      </div>
    );
  },
);
